/**
 * The plant domain that owns an asset, picked from the drawing's own account.
 *
 * An asset whose owning domain is not the drawing's is owned by another plant:
 * the drawing shows it, and that plant owns it. "Others" is for an asset no
 * plant in the register owns.
 *
 * `GET /domains` carries every account's domains, and a reviewer picking an
 * owner for one drawing's asset has no business seeing another customer's
 * plants — so the drawing's own domain resolves the account through the domain
 * index, and only that account's domains are offered. A drawing with no plant
 * resolves no account, so it offers "Others" beside the "Unassigned" that names
 * its own, rather than falling back to the whole listing.
 *
 * The picker is a write control, so it refuses to change the owner until the
 * account's plants are known. Before then the list would be the stored value
 * and "Others" alone, which reads as an account with one plant — and a read
 * failure would read the same way, letting a reviewer name "Others" for an
 * asset whose real owner is on the list nobody could fetch.
 *
 * Renders the `<select>`, plus one line naming what the picker is waiting on
 * while it waits. The label is the caller's, tied to `id`, and so is the row
 * around both — which is what keeps the two dialogs' looks their own.
 */
import React, { useMemo } from 'react';
import { OWNER_DOMAIN_OTHERS, effectiveOwnerDomainId } from '../types';
import { useDomainIndex } from '../hooks/useDomainIndex';
import { domainPathLabel, type DomainIndex } from '../utils/domainTree';
import { domainLabel } from '../utils/documentLabels';

interface DomainSelectProps {
  /** The `<select>`'s id, which the caller's own `<label>` points `htmlFor` at. */
  id: string;
  /** What the asset holds: a domain id, `OWNER_DOMAIN_OTHERS`, or empty. */
  storedValue: string;
  onChange: (next: string) => void;
  /** The drawing's own `domain_id`, whose account scopes the options. */
  drawingDomainId: string;
  /** The drawing's own plant name, which labels its domain before the listing lands. */
  drawingPlantName: string | null;
  disabled?: boolean;
  style?: React.CSSProperties;
}

/**
 * The drawing's own domain is named on the drawing, so it reads the same here as
 * it does in the drawings list from the first paint. Any other domain has only
 * its identifier until the listing arrives.
 */
function ownerDomainLabel(
  index: DomainIndex,
  domainId: string,
  drawingDomainId: string,
  drawingPlantName: string | null,
): string {
  const fallback = domainId === drawingDomainId ? domainLabel(drawingPlantName) : domainId;
  return domainPathLabel(index, domainId, fallback);
}

const DomainSelect: React.FC<DomainSelectProps> = ({
  id,
  storedValue,
  onChange,
  drawingDomainId,
  drawingPlantName,
  disabled = false,
  style,
}) => {
  const { index: domainIndex, loading, error } = useDomainIndex();
  const value = effectiveOwnerDomainId(storedValue, drawingDomainId);
  const listingUnavailable = loading || Boolean(error);

  const options = useMemo(() => {
    const accountId = drawingDomainId ? domainIndex.get(drawingDomainId)?.accountId : undefined;
    const rows = accountId
      ? [...domainIndex.values()]
          .filter((node) => node.accountId === accountId)
          .map((node) => ({
            id: node.id,
            label: ownerDomainLabel(domainIndex, node.id, drawingDomainId, drawingPlantName),
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : [];
    // A stored domain the account's list does not carry — the listing has not
    // arrived, or the owner was named from outside this account — still has to
    // show as what the asset holds.
    if (value && value !== OWNER_DOMAIN_OTHERS && !rows.some((row) => row.id === value)) {
      rows.unshift({
        id: value,
        label: ownerDomainLabel(domainIndex, value, drawingDomainId, drawingPlantName),
      });
    }
    return rows;
  }, [domainIndex, drawingDomainId, drawingPlantName, value]);

  return (
    <>
      <select
        id={id}
        className="pnid-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || listingUnavailable}
        style={style}
      >
        {/* Empty names the drawing's own plant, which a drawing with no plant
            names as none. It stays on offer there so that naming "Others" is a
            step a reviewer can take back; every other drawing names its plant,
            and offers that by name instead. */}
        {!drawingDomainId && <option value="">Unassigned</option>}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
        <option value={OWNER_DOMAIN_OTHERS}>Others</option>
      </select>
      {listingUnavailable && (
        <div
          style={{
            fontSize: 11,
            marginTop: 4,
            color: error ? 'var(--raven-warning-text)' : 'var(--raven-text-secondary)',
          }}
        >
          {error
            ? `The plant list could not be read, so the owner cannot be changed — ${error}`
            : 'Loading this account’s plants…'}
        </div>
      )}
    </>
  );
};

export default DomainSelect;
